import { useId, useState } from "react";
import Modal from "@/components/common/Modal";
import { buttonClass, chipClass } from "@/components/common/controls";
import { ERROR } from "@/components/envoy/dialog/styles";
import { SparkIcon } from "@/components/icons";
import { GRAD_BUTTON } from "@/components/site/styles";
import { actionProps } from "@/lib/controlAnalytics";
import { charCount } from "@/lib/envoyForm";
import {
    MAX_TAILOR_INSTRUCTIONS,
    instructionsFault,
    tailorChoices,
    tailorKeywords,
    toggledKeyword,
    type TailorChoices,
} from "@/lib/posting/tailorChoices";
import { GroupLabel } from "./parts";

/** A posting to tailor for, whether or not it has an application yet. */
export interface TailorPosting {
    posting_id: string;
    title: string;
    /** Absent when the posting's own record is not there to say. */
    company?: string | null;
}

const source = "TailorDialog";

/** Under the box: where its text came from, and how far a change reaches. */
function boxLine(saved: string | null, changed: boolean): string {
    if (!saved) return "For this resume only.";
    return changed
        ? "Changed for this resume. Your envoy keeps its own."
        : "From your envoy. Changes here are for this resume only.";
}

/**
 * Tailor my resume, asked before a tailor is spent: which of the keywords
 * on the resume to emphasize, which of the ones it does not say the person
 * has done and wants added to it, and the instructions for this resume,
 * which start as the saved ones. A tailor still adds nothing: a keyword
 * tapped here reaches the resume's skills first. Open while `posting` is
 * set; each posting asked about starts fresh.
 */
export default function TailorDialog({
    posting,
    covered,
    missing,
    savedInstructions,
    model,
    cost,
    busy = false,
    onConfirm,
    onClose,
}: {
    posting: TailorPosting | null;
    /** The keywords the resume says, in the order the posting names them. */
    covered: readonly string[];
    /** The keywords the resume does not say, in the order the posting names them. */
    missing: readonly string[];
    savedInstructions: string | null;
    model: string;
    cost: string | null;
    /** The tailor is being added: confirming again does nothing. */
    busy?: boolean;
    /** The choices, and the keywords the resume does not say that the person tapped to add. */
    onConfirm: (choices: TailorChoices, added: string[]) => void;
    onClose: () => void;
}) {
    const ids = useId();
    const postingId = posting?.posting_id ?? null;
    const [off, setOff] = useState<readonly string[]>([]);
    const [picked, setPicked] = useState<readonly string[]>([]);
    // Null until the person types, so saved instructions that arrive late still show.
    const [typed, setTyped] = useState<string | null>(null);
    const [asked, setAsked] = useState(postingId);
    if (postingId !== asked) {
        setAsked(postingId);
        setOff([]);
        setPicked([]);
        setTyped(null);
    }

    const terms = tailorKeywords(covered);
    const absent = tailorKeywords(missing).filter((term) => !terms.includes(term));
    const text = typed ?? savedInstructions ?? "";
    const fault = instructionsFault(text);
    const choices = tailorChoices(terms, off, text, savedInstructions);
    // In the posting's order, whatever order they were tapped in.
    const added = absent.filter((term) => picked.includes(term));
    const context = { source, posting_id: postingId ?? undefined };

    return (
        <Modal
            analyticsContext={context}
            open={posting !== null}
            onClose={onClose}
            width={560}
            title="Tailor my resume"
            description={
                posting &&
                (posting.company ? `${posting.title} at ${posting.company}` : posting.title)
            }
            footer={
                <>
                    <button
                        {...actionProps("tailor_cancelled", context)}
                        type="button"
                        onClick={onClose}
                        className={buttonClass("ghost", "lg", "h-[38px] rounded-[11px]")}
                    >
                        Cancel
                    </button>
                    <button
                        {...actionProps("tailor_confirmed", {
                            ...context,
                            count: choices.keywordsOff.length,
                            next_state: choices.instructions !== undefined,
                        })}
                        type="button"
                        disabled={fault !== undefined || busy}
                        onClick={() => onConfirm(choices, added)}
                        className={`${GRAD_BUTTON} h-[38px] gap-2 rounded-[11px] px-4 text-[13px]`}
                    >
                        <SparkIcon className="size-4" />
                        {busy ? "Adding…" : "Tailor my resume"}
                    </button>
                </>
            }
        >
            <div className="flex flex-col gap-5">
                {terms.length > 0 && (
                    <div
                        role="group"
                        aria-labelledby={`${ids}-keywords`}
                        className="flex flex-col gap-2"
                    >
                        <div className="flex items-baseline justify-between gap-3">
                            <span id={`${ids}-keywords`} className="text-[13px] font-semibold">
                                Keywords to emphasize
                            </span>
                            <span className="whitespace-nowrap font-mono text-[11px] text-fg-faint">
                                {terms.length - choices.keywordsOff.length} of {terms.length}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {terms.map((term, index) => {
                                const on = !off.includes(term);
                                return (
                                    <button
                                        {...actionProps("tailor_keyword_toggled", {
                                            ...context,
                                            index,
                                            next_state: !on,
                                        })}
                                        key={term}
                                        type="button"
                                        aria-pressed={on}
                                        onClick={() => setOff(toggledKeyword(off, term))}
                                        className={chipClass(on)}
                                    >
                                        {term}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                {absent.length > 0 && (
                    <div
                        role="group"
                        aria-labelledby={`${ids}-absent`}
                        className="flex flex-col gap-2"
                    >
                        <span id={`${ids}-absent`}>
                            <GroupLabel>Not on your resume</GroupLabel>
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                            {absent.map((term, index) => {
                                const on = picked.includes(term);
                                return (
                                    <button
                                        {...actionProps("tailor_skill_toggled", {
                                            ...context,
                                            index,
                                            next_state: !on,
                                        })}
                                        key={term}
                                        type="button"
                                        aria-pressed={on}
                                        onClick={() => setPicked(toggledKeyword(picked, term))}
                                        className={chipClass(on)}
                                    >
                                        {term}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="m-0 text-[12.5px] text-fg-faint">
                            {added.length > 0
                                ? "Added to your resume's skills when you tailor."
                                : "Tap one you have done to add it to your resume."}
                        </p>
                    </div>
                )}
                <div className="flex flex-col gap-[5px]">
                    <label
                        htmlFor={`${ids}-instructions`}
                        className="flex items-baseline justify-between gap-1.5 text-[13px] font-semibold"
                    >
                        <span>Instructions for this resume</span>
                        <span
                            className={`whitespace-nowrap font-mono text-[11px] ${fault ? "font-semibold text-negative" : "font-normal text-fg-faint"}`}
                        >
                            {charCount(text)}/{MAX_TAILOR_INSTRUCTIONS}
                        </span>
                    </label>
                    <textarea
                        {...actionProps("tailor_instructions_edited", context)}
                        id={`${ids}-instructions`}
                        rows={3}
                        value={text}
                        placeholder="Lead with the platform work, play down the consulting years"
                        onChange={(event) => setTyped(event.target.value)}
                        aria-invalid={Boolean(fault) || undefined}
                        className={`resize-y rounded-[11px] border bg-card px-[11px] py-[9px] text-[13px] font-medium leading-normal text-fg outline-none placeholder:text-fg-faint focus:border-green focus:outline-2 focus:-outline-offset-1 focus:outline-[rgba(33,212,92,.55)] ${
                            fault ? "border-negative" : "border-line-control"
                        }`}
                    />
                    {fault ? (
                        <span className={ERROR}>{fault}</span>
                    ) : (
                        <span className="text-[12.5px] text-fg-faint">
                            {boxLine(savedInstructions, choices.instructions !== undefined)}
                        </span>
                    )}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-small text-fg-muted">
                    <span>
                        Model <span className="ml-1 font-semibold text-fg">{model}</span>
                    </span>
                    {cost && <span>{cost}</span>}
                </div>
            </div>
        </Modal>
    );
}
